/** @jsxImportSource @emotion/react */
import { Grid as MuiGrid } from '@mui/material';
import { DateValidationError } from '@mui/x-date-pickers';
import {
  Buttons,
  Spinner,
  TextFields,
  Widgets,
} from '@zip/business-components';
import { BasePage } from 'components';
import { useSnackbar } from 'contexts';
import { PageAction } from 'enums';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { PageProps } from 'types';
import { logError, useMerchantProfile, useReportFunctions } from 'utils';

const yesterday = moment().subtract(1, 'days').startOf('day');

const DisbursementPage: FC<PageProps> = () => {
  const Snackbar = useSnackbar();
  const ReportFunctions = useReportFunctions();
  const MerchantProfileFunctions = useMerchantProfile();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contact] = useState(MerchantProfileFunctions.getContactFromClaims());

  const [isScheduleSubmitting, setIsScheduleSubmitting] =
    useState<boolean>(false);
  const [isGenerateSubmitting, setIsGenerateSubmitting] =
    useState<boolean>(false);

  const [dailyChecked, setDailyChecked] = useState<boolean>(false);
  const [weeklyChecked, setWeeklyChecked] = useState<boolean>(false);
  const [monthlyChecked, setMonthlyChecked] = useState<boolean>(false);

  const [fromDate, setFromDate] = useState<Moment>(yesterday);
  const [toDate, setToDate] = useState<Moment>(yesterday);
  const [isDateRangeValid, setIsDateRangeValid] = useState<boolean>(true);
  const [fileName, setFileName] = useState<string>();

  const [dateValidationError, setDateValidationError] =
    useState<DateValidationError | null>(null);

  async function submitScheduleSettings(): Promise<void> {
    if (isScheduleSubmitting) {
      return;
    }

    setIsScheduleSubmitting(true);
    const payload = {
      isDaily: dailyChecked,
      isWeekly: weeklyChecked,
      isMonthly: monthlyChecked,
    };

    const result = await ReportFunctions.updateScheduleSettings(payload);
    if (result.isSuccess) {
      Snackbar.success('Disbursement report settings updated successfully');
    } else {
      Snackbar.error(
        'An error occurred updating your disbursement report settings. Please try again'
      );
    }
    setIsScheduleSubmitting(false);
  }

  async function submitGenerateReport(): Promise<void> {
    if (isGenerateSubmitting) {
      return;
    }

    setIsGenerateSubmitting(true);
    setFileName(null);

    try {
      const result = await ReportFunctions.generateReport(
        moment(fromDate),
        moment(toDate)
      );
      if (result) {
        setFileName(result);
        Snackbar.success('Report generated successfully');
      } else {
        Snackbar.error(
          'An error occurred generating your report. Please try again'
        );
      }
    } catch (err) {
      logError(PageAction.generateDisbursementReport, err, {
        fromDate,
        toDate,
      });
      Snackbar.error(
        'An unexpected error occurred generating your report. Please try again with different dates'
      );
    } finally {
      setIsGenerateSubmitting(false);
    }
  }

  function blobifyCsv(result): void {
    let csv = result?.csvData;
    const isMac = !!navigator?.platform.match(/(Mac|iPhone|iPod|iPad)/i);

    if (!isMac) {
      const BOM = '\ufeff';
      csv = BOM + csv;
    }

    const blob = new Blob([csv], {
      type: 'text/csv;charset=UTF-8',
    });
    const blobUrl = window.URL.createObjectURL(blob);

    // @ts-ignore to cover TS2339
    if (window.navigator && window.navigator?.msSaveOrOpenBlob) {
      // IE
      // @ts-ignore to cover TS2339
      window.navigator?.msSaveOrOpenBlob(blob, result?.fileName);
    } else {
      // Chrome & Firefox
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = result?.fileName;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      a.remove();
    }
  }

  async function downloadReport(): Promise<void> {
    const result = await ReportFunctions.fetchReport(fileName);
    if (result) {
      blobifyCsv(result);
    } else {
      Snackbar.error(
        'An error occurred fetching your report. Please try again'
      );
    }
  }

  function updateState(updatedSchedule): void {
    setDailyChecked(updatedSchedule?.isDaily);
    setWeeklyChecked(updatedSchedule?.isWeekly);
    setMonthlyChecked(updatedSchedule?.isMonthly);
  }

  const getMaxToDate = (): Date => {
    const maxDate = moment(fromDate);
    maxDate.add(3, 'month');
    return _.min([maxDate, yesterday]);
  };

  const validateDateRange = (): void => {
    const threeMonths = moment(fromDate);
    threeMonths.add(3, 'month');
    setIsDateRangeValid(fromDate <= toDate && toDate <= threeMonths);
  };

  useEffect(() => {
    setFileName(null);
    validateDateRange();
  }, [fromDate, toDate]);

  useEffect(() => {
    async function getScheduleSettings(): Promise<void> {
      setIsLoading(true);
      try {
        const result = await ReportFunctions.getScheduleSettings();
        if (result?.disbursementSchedule) {
          updateState(result?.disbursementSchedule);
        }
      } catch (err) {
        logError(PageAction.loadDisbursementSchedule, err);
      }
      setIsLoading(false);
    }

    getScheduleSettings();
  }, []);

  const dateValidationErrorMessage = useMemo(() => {
    switch (dateValidationError) {
      case 'minDate': {
        return 'Date must be after start date';
      }
      case 'maxDate': {
        return 'Date range cannot exceed 3 months';
      }
      default: {
        return '';
      }
    }
  }, [dateValidationError]);

  return (
    <BasePage title="Disbursement reports">
      {isLoading && <Spinner />}
      {!isLoading && (
        <MuiGrid container sx={{ flexGrow: 1 }} spacing={2}>
          <MuiGrid size={{ xs: 12 }}>
            <h2>Report schedule</h2>
            <p>
              Click the Daily, Weekly or Monthly checkboxes to receive Zip CSV
              disbursement reports to:
              <br />
              <strong className="fs-mask">{contact.contactEmail}</strong>.
            </p>
            <MuiGrid container sx={{ flexGrow: 1 }} spacing={2}>
              <MuiGrid size={{ xs: 6 }}>
                <Widgets.Checkbox
                  label="Daily"
                  checked={dailyChecked}
                  onChange={(e, checked): void => setDailyChecked(checked)}
                />
              </MuiGrid>
              <MuiGrid size={{ xs: 6 }}>
                <Widgets.Checkbox
                  label="Weekly"
                  checked={weeklyChecked}
                  onChange={(e, checked): void => setWeeklyChecked(checked)}
                />
              </MuiGrid>
              <MuiGrid size={{ xs: 6 }}>
                <Widgets.Checkbox
                  label="Monthly"
                  checked={monthlyChecked}
                  onChange={(e, checked): void => setMonthlyChecked(checked)}
                />
              </MuiGrid>
              <MuiGrid size={{ xs: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Buttons.Primary
                    onClick={(): Promise<void> => submitScheduleSettings()}
                    loading={isScheduleSubmitting}
                  >
                    Save
                  </Buttons.Primary>
                </div>
              </MuiGrid>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid size={{ xs: 12 }}>
            <h2>Generate CSV report</h2>
            <p>Select a date range (search up to 3 months)</p>
            <MuiGrid
              container
              sx={{ flexGrow: 1 }}
              spacing={2}
              justifyContent="flex-end"
            >
              <MuiGrid size={{ xs: 6, sm: 4 }}>
                <TextFields.Date
                  slotProps={{
                    openPickerButton: {
                      'aria-label': 'open calendar',
                    },
                    textField: {
                      id: 'fromDate',
                      placeholder: 'DD/MM/YYYY',
                    },
                  }}
                  data-testid="fromDate"
                  label="Start date"
                  name="fromDate"
                  onChange={(value): void =>
                    // @material-ui/pickers sends the value to onChange, not the event object ಠ_ಠ
                    setFromDate(value)
                  }
                  value={fromDate}
                  disableFuture
                  maxDate={yesterday}
                  views={['year', 'month', 'day']}
                />
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 4 }}>
                <TextFields.Date
                  slotProps={{
                    openPickerButton: {
                      'aria-label': 'open calendar',
                    },
                    textField: {
                      id: 'toDate',
                      placeholder: 'DD/MM/YYYY',
                      helperText: dateValidationErrorMessage,
                    },
                  }}
                  data-testid="toDate"
                  label="End date"
                  name="toDate"
                  onChange={(value): void =>
                    // @material-ui/pickers sends the value to onChange, not the event object ಠ_ಠ
                    setToDate(value)
                  }
                  value={toDate}
                  disableFuture
                  views={['year', 'month', 'day']}
                  minDate={fromDate}
                  maxDate={getMaxToDate()}
                  onError={(error) => setDateValidationError(error)}
                />
              </MuiGrid>
              <MuiGrid size={{ xs: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                  }}
                >
                  <Buttons.Primary
                    onClick={(): Promise<void> => submitGenerateReport()}
                    loading={isGenerateSubmitting}
                    disabled={!isDateRangeValid}
                  >
                    Create
                  </Buttons.Primary>
                  {fileName && (
                    <Buttons.Tertiary
                      onClick={(): Promise<void> => downloadReport()}
                    >
                      Download
                    </Buttons.Tertiary>
                  )}
                </div>
              </MuiGrid>
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      )}
    </BasePage>
  );
};

export default DisbursementPage;
